<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const instances = ref([]);
const loading = ref(true);
const loadError = ref('');
const selectedInstanceName = ref('');
const inboxName = ref('');

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const selectableInstances = computed(() =>
  instances.value.filter(
    instance => instance.connectionStatus === 'open' && instance.number
  )
);

const selectedInstance = computed(() =>
  instances.value.find(instance => instance.name === selectedInstanceName.value)
);

onMounted(async () => {
  try {
    const response = await InboxesAPI.getEvolutionInstances();
    instances.value = response.data;
  } catch (error) {
    loadError.value =
      error.message ||
      t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
});

const selectInstance = instance => {
  selectedInstanceName.value = instance.name;
  inboxName.value = instance.profileName || instance.name;
};

const statusLabel = instance => {
  if (instance.connectionStatus === 'open') {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_OPEN');
  }
  if (instance.connectionStatus === 'connecting') {
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_CONNECTING');
  }
  return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.STATUS_CLOSED');
};

const createChannel = async () => {
  if (!selectedInstance.value) {
    return;
  }

  try {
    const channel = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value?.trim() || selectedInstance.value.name,
      channel: {
        type: 'whatsapp',
        phone_number: selectedInstance.value.number,
        provider: 'evolution_api',
        provider_config: { instance_name: selectedInstance.value.name },
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: channel.id },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div>
    <div class="mb-8">
      <h2 class="mb-1 text-base font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.DESC') }}
      </p>
    </div>

    <div v-if="loading" class="py-8 text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.LOADING') }}
    </div>

    <div v-else-if="loadError" class="py-8 text-sm text-n-slate-11">
      {{ loadError }}
    </div>

    <div v-else-if="selectableInstances.length === 0" class="py-8">
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.EMPTY') }}
      </p>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION.EMPTY_DESC') }}
      </p>
    </div>

    <div v-else class="flex flex-col gap-2">
      <button
        v-for="instance in selectableInstances"
        :key="instance.id"
        type="button"
        class="flex items-center gap-3 px-4 py-3 text-left rounded-xl border transition-all duration-200"
        :class="
          selectedInstanceName === instance.name
            ? 'border-n-blue-9 bg-n-blue-2'
            : 'border-n-weak hover:border-n-blue-9'
        "
        @click="selectInstance(instance)"
      >
        <div
          class="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-n-alpha-2"
        >
          <img
            v-if="instance.profilePicUrl"
            :src="instance.profilePicUrl"
            alt=""
            class="size-full object-cover"
          />
          <span v-else class="text-sm font-medium text-n-slate-11">
            {{ (instance.profileName || instance.name || '?').charAt(0) }}
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-sm font-medium text-n-slate-12">
            {{ instance.profileName || instance.name }}
          </p>
          <p class="truncate text-xs text-n-slate-11">{{ instance.number }}</p>
        </div>
        <span
          class="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          :class="
            instance.connectionStatus === 'open'
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-11'
          "
        >
          {{ statusLabel(instance) }}
        </span>
      </button>
    </div>

    <form
      v-if="selectedInstance"
      class="mt-6 flex flex-col gap-4"
      @submit.prevent="createChannel"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            class="rounded-xl border border-n-weak px-3 py-2 text-sm"
            :placeholder="
              t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')
            "
          />
        </label>
      </div>

      <div class="w-full">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
